'use client';

import {ComboboxInput, type ComboboxOption} from 'common/components/combobox_input';
import {X} from 'lucide-react';
import {useEffect, useState, type ReactNode} from 'react';

export interface MultiComboboxOption {
  value: string;
  label: string;
}

// Renderer for the selected items. Receives the selected options and a remove
// callback so callers can fully customize the chips (see companies step).
type RenderSelected<T extends MultiComboboxOption> = (
  options: T[],
  onRemove: (value: string) => void,
) => ReactNode;

interface MultiComboboxInputProps<T extends MultiComboboxOption> {
  label: string;
  placeholder?: string;
  value: string[];
  onChange: (options: T[]) => void;
  fetchOptions: () => Promise<T[]>;
  // Overrides how selected items are displayed. Defaults to orange pills.
  renderSelected?: RenderSelected<T>;
}

const defaultRenderSelected: RenderSelected<MultiComboboxOption> = (options, onRemove) => (
  <div className='flex flex-wrap gap-2'>
    {options.map((o) => (
      <span
        key={o.value}
        className='inline-flex items-center gap-1.5 px-3 py-1 bg-iq-orange-light text-iq-orange-dark text-sm font-medium rounded-full'>
        {o.label}
        <button
          type='button'
          onClick={() => onRemove(o.value)}
          className='hover:opacity-70 transition-opacity'
          aria-label={`Remove ${o.label}`}>
          <X className='w-3 h-3' />
        </button>
      </span>
    ))}
  </div>
);

function MultiComboboxInput<T extends MultiComboboxOption>({
  label,
  placeholder,
  value,
  onChange,
  fetchOptions,
  renderSelected = defaultRenderSelected,
}: MultiComboboxInputProps<T>) {
  // The full option objects, loaded once on first focus. We assume the option set is
  // always available, so a non-empty state means "already loaded".
  const [options, setOptions] = useState<T[]>([]);
  const selectedOptions = options.filter((o) => value.includes(o.value));

  // If we mount with a preselected value, eagerly load options so the selected
  // options can be resolved and rendered — otherwise focus is the only trigger.
  useEffect(() => {
    if (value.length > 0) handleFetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFetchOptions() {
    if (options.length > 0) return;
    setOptions(await fetchOptions());
  }

  function handleChange(option: ComboboxOption<string>) {
    // Map back to the full option (the inner ComboboxInput only round-trips {value, label}).
    const full = options.find((o) => o.value === option.value);
    if (!full) {
      console.warn(
        `MultiComboboxInput: picked value "${option.value}" not found in loaded options`,
      );
      return;
    }
    onChange([...selectedOptions, full]);
  }

  function handleRemove(value: string) {
    onChange(selectedOptions.filter((o) => o.value !== value));
  }

  // Hide already-selected options from the dropdown so they can't be picked twice.
  const filteredOptions = options.filter((o) => !value.includes(o.value));

  return (
    <div className='flex flex-col gap-2'>
      <ComboboxInput
        label={label}
        placeholder={placeholder ?? `Search ${label.toLowerCase()}…`}
        value=''
        options={filteredOptions}
        onChange={handleChange}
        onFetchOptions={handleFetchOptions}
        clearOnSelect
        allowCustom={false}
      />
      {selectedOptions.length > 0 && renderSelected(selectedOptions, handleRemove)}
    </div>
  );
}

export {MultiComboboxInput};
